import { CustomerServiceOutlined, MinusOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import ContactSupportDrawer from "./ContactSupportDrawer";

/** Remembers, in this browser, that the card was minimised (as Verdax's). */
const MINIMISED_STORAGE_KEY = "help-centre-contact-minimised";

const readMinimised = () => {
	try {
		return localStorage.getItem(MINIMISED_STORAGE_KEY) === "true";
	} catch {
		return false;
	}
};

const saveMinimised = (minimised: boolean) => {
	try {
		localStorage.setItem(MINIMISED_STORAGE_KEY, String(minimised));
	} catch {
		// Storage is blocked: the choice lasts until the page is left.
	}
};

/**
 * The "Contact support" card at the foot of every Help Centre page, sticky at
 * the bottom of the page as it scrolls, or a round button when minimised. The
 * drawer is rendered either way, so minimising never loses a draft.
 */
function ContactSupport() {
	const [isMinimised, setMinimised] = useState(readMinimised);
	const [isDrawerOpen, setDrawerOpen] = useState(false);
	const restoreButton = useRef<HTMLButtonElement>(null);
	const contactButton = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
	// Focus follows a minimise or restore, but not the first render.
	const moveFocus = useRef(false);

	useEffect(() => {
		if (!moveFocus.current) return;
		moveFocus.current = false;
		(isMinimised ? restoreButton.current : contactButton.current)?.focus();
	}, [isMinimised]);

	const minimise = (minimised: boolean) => {
		moveFocus.current = true;
		setMinimised(minimised);
		saveMinimised(minimised);
	};

	return (
		<>
			<aside
				className={`help-contact${isMinimised ? " help-contact--minimised" : ""}`}
				aria-label="Contact support"
			>
				{isMinimised ? (
					<Tooltip title="Contact support" placement="left">
						<button
							ref={restoreButton}
							type="button"
							className="help-contact__restore"
							aria-label="Show contact support"
							onClick={() => minimise(false)}
						>
							<CustomerServiceOutlined aria-hidden="true" />
						</button>
					</Tooltip>
				) : (
					<div className="help-contact__card">
						<p className="help-contact__text">
							<CustomerServiceOutlined aria-hidden="true" />
							<span>
								Still need help? Contact our support team and we&apos;ll be
								happy to assist.
							</span>
						</p>
						<div className="help-contact__actions">
							<Button
								ref={contactButton}
								type="primary"
								onClick={() => setDrawerOpen(true)}
							>
								Contact support
							</Button>
							<Tooltip title="Minimise">
								<Button
									type="text"
									className="help-contact__minimise"
									icon={<MinusOutlined aria-hidden="true" />}
									aria-label="Minimise contact support"
									onClick={() => minimise(true)}
								/>
							</Tooltip>
						</div>
					</div>
				)}
			</aside>
			<ContactSupportDrawer
				open={isDrawerOpen}
				onClose={() => setDrawerOpen(false)}
			/>
		</>
	);
}

export default ContactSupport;
